import React, { useEffect, useState } from 'react'
import { Col, Form, FormInstance, Radio, Row, Input, Switch } from 'antd'
import MapModal from 'helper/Map/MapModal'
import { Info } from '@phosphor-icons/react'
import { Address } from 'types/globalTypes'
import {
  useCreateMentorRequestForEvent,
  useUpdateMentorRequestForEvent,
} from 'api/Event/MentorRequest'
import { creationEventStore } from 'state/event/eventCreationStore'
import { MentorRequestForEvent } from 'types/mentorRequest/mentorRequest'
import { myStore } from 'state/store'

interface GeneralDetailsProps {
  form: FormInstance
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  reference: 'event' | 'league' | 'class'
  referenceId: string
  prevData: MentorRequestForEvent
}

const GeneralDetails: React.FC<GeneralDetailsProps> = ({
  form,
  setCurrentStep,
  reference,
  referenceId,
  prevData,
}) => {
  const setModalBtnLoader = myStore(state => state.setModalBtnLoader)
  const [mapModalVisible, setMapModalVisible] = useState(false)
  const [mapData, setMapModalData] = useState<Address>(
    prevData ? prevData.address : undefined,
  )

  const setFieldsValue = (data: any) => {
    if (data?.address) form.setFieldsValue({ location: data?.address })
  }

  const { mutate: createMentorRequestForEventMutation } =
    useCreateMentorRequestForEvent()
  const { mutate: updateMentorRequestForEventMutation } =
    useUpdateMentorRequestForEvent()

  const formSchema = {
    genderPreference: prevData ? prevData.genderPreference : 'male',
    sessionType: prevData ? prevData.sessionType : 'physical',
    hasPreferredMentors: prevData ? prevData.hasPreferredMentors : false,
    location: prevData
      ? `${prevData.address?.city}, ${prevData.address?.country}`
      : undefined,
    preferredMentorName: prevData ? prevData.preferredMentorName : undefined,
  }

  const handleFinish = values => {
    const payload = {
      ...values,
      address: mapData ? mapData : undefined,
      [`${reference}Id`]: referenceId,
    }
    setModalBtnLoader(true)

    if (prevData) {
      updateMentorRequestForEventMutation(
        {
          payload,
          requestId: prevData._id,
        },
        {
          onSuccess: data => {
            setCurrentStep(1)
            setModalBtnLoader(false)
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    } else {
      createMentorRequestForEventMutation(payload, {
        onSuccess: data => {
          setCurrentStep(1)
          setModalBtnLoader(false)
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      })
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        className="mentor-request-form"
        initialValues={formSchema}
        onFinish={handleFinish}
      >
        <Row className="mt-20px">
          <Col>
            <label htmlFor="" className="input-label required-input">
              Gender Preferences
            </label>
            <Form.Item
              name="genderPreference"
              rules={[
                {
                  required: true,
                  message: 'Gender preference is required',
                },
              ]}
            >
              <Radio.Group className="flex">
                <Radio className="radio" value="male">
                  Male
                </Radio>
                <Radio className="radio" value="female">
                  Female
                </Radio>
                <Radio className="radio" value="most-qualified">
                  Most Qualified
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col>
            <label className="input-label required-input">Session type</label>
            <Form.Item
              name="sessionType"
              rules={[
                {
                  required: true,
                  message: 'Session type is required',
                },
              ]}
            >
              <Radio.Group className="flex">
                <Radio className="radio" value="physical">
                  In person
                </Radio>
                <Radio className="radio" value="online">
                  Online
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.sessionType !== currentValues.sessionType
          }
        >
          {() =>
            form.getFieldValue('sessionType') === 'physical' && (
              <Row>
                <Col>
                  <label className="input-label required-input">Location</label>
                  <Form.Item
                    name="location"
                    rules={[
                      {
                        required: true,
                        message: 'Location is required for in-person sessions',
                      },
                    ]}
                  >
                    <Input
                      readOnly
                      placeholder="Select location"
                      className="form-control-field"
                      onClick={() => setMapModalVisible(true)}
                      value={
                        form.getFieldValue('mapData')
                          ? `${form.getFieldValue('mapData').city}, ${
                              form.getFieldValue('mapData').state
                            }`
                          : ''
                      }
                    />
                  </Form.Item>
                </Col>

                {mapModalVisible && (
                  <MapModal
                    mapData={mapData}
                    setMapModalVisible={setMapModalVisible}
                    setMapModalData={setMapModalData}
                    setFieldsValue={setFieldsValue}
                  />
                )}
              </Row>
            )
          }
        </Form.Item>

        <Row>
          <Col span={24} className="flex justify-between">
            <label className="mt-5px input-label">
              Do you have any preferred Mentors?
            </label>
            <Form.Item name="hasPreferredMentors" valuePropName="checked">
              <Switch className="custom-switch-btn" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.hasPreferredMentors !== currentValues.hasPreferredMentors
          }
        >
          {() =>
            form.getFieldValue('hasPreferredMentors') && (
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="preferredMentorName"
                    rules={[
                      {
                        required: true,
                        message: 'Preferred mentor name is required',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Mentor name"
                      className="form-control-field"
                    />
                  </Form.Item>
                </Col>
              </Row>
            )
          }
        </Form.Item>
      </Form>
    </>
  )
}

export default GeneralDetails
